import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import {
  getClinicalCases,
  getClinicalCaseById,
  createClinicalCase,
  updateClinicalCase,
  deleteClinicalCase,
  uploadClinicalCaseImage,
  deleteClinicalCaseImage,
  setClinicalCaseQuestions,
} from "../../../services/ClinicalCaseService";
import { getQuestions } from "../../../services/QuestionService";
import { IMAGE_ACCEPT, resolveUploadUrl, validateImageFile } from "../../../utils/uploads";
import { getApiErrorMessage } from "../../../utils/apiError";
import { getSubjects } from "../../../services/SubjectService";
import { Trans, useTranslation } from "react-i18next";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";

const emptyForm = {
  subject_id: "",
  title: "",
  stem: "",
  image_url: "",
  is_active: true,
};

const ClinicalCases = () => {
  const { t } = useTranslation();
  const can = usePermissions();
  const canCreate = can(PERMISSIONS.CLINICAL_CASE_CREATE);
  const canUpdate = can(PERMISSIONS.CLINICAL_CASE_UPDATE);
  const canDelete = can(PERMISSIONS.CLINICAL_CASE_DELETE);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [subjects, setSubjects] = useState([]);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");

  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Image d'illustration (même fonctionnement que pour les questions) : fichier choisi,
  // aperçu, image déjà enregistrée et demande de retrait.
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [existingImage, setExistingImage] = useState("");
  const [removeImage, setRemoveImage] = useState(false);
  const [imageError, setImageError] = useState("");

  // Affectation des questions au cas (liste ordonnée = ordre d'affichage progressif).
  const [assignCase, setAssignCase] = useState(null);
  const [assigned, setAssigned] = useState([]);
  const [bank, setBank] = useState([]);
  const [bankSearch, setBankSearch] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignError, setAssignError] = useState("");

  // Aperçu des questions rattachées, affichées dans l'ordre progressif.
  const [viewCase, setViewCase] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  useEffect(() => {
    getSubjects({ page: 1, limit: 200 }).then((res) => setSubjects(res.data?.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: "created_at", sort_dir: "desc" };
      if (search) params.search = search;
      if (subjectFilter) params.subject_id = subjectFilter;

      const res = await getClinicalCases(params);
      const body = res.data;
      setCases(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(getApiErrorMessage(err, t("pages.clinicalCases.impossibleChargerCasCliniques")));
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, subjectFilter, t]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const label = (list, id) => list.find((x) => x.id === id)?.name || "-";

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setSubjectFilter("");
    setPage(1);
  };

  const resetImageState = (existing = "") => {
    setImageFile(null);
    setImagePreview((prev) => {
      if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
      return "";
    });
    setExistingImage(existing);
    setRemoveImage(false);
    setImageError("");
  };

  const handleImageSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // permet de re-sélectionner le même fichier
    if (!file) return;
    const problem = validateImageFile(file);
    if (problem) {
      setImageError(t(problem === "type" ? "pages.questions.imageTypeInvalid" : "pages.questions.imageTooLarge"));
      return;
    }
    setImageError("");
    setImageFile(file);
    setRemoveImage(false);
    setImagePreview((prev) => {
      if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleImageRemove = () => {
    setImageFile(null);
    setImagePreview((prev) => {
      if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
      return "";
    });
    setRemoveImage(!!existingImage);
    setImageError("");
  };

  const openAddModal = () => {
    resetImageState();
    setFormMode("add");
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError("");
    setEditingId(null);
    setShowFormModal(true);
  };

  const openEditModal = (clinicalCase) => {
    resetImageState(clinicalCase.image_url || "");
    setFormMode("edit");
    setFormData({
      subject_id: clinicalCase.subject_id || "",
      title: clinicalCase.title || "",
      stem: clinicalCase.stem || "",
      image_url: "",
      is_active: !!clinicalCase.is_active,
    });
    setFormErrors({});
    setSubmitError("");
    setEditingId(clinicalCase.id);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (submitting) return;
    setShowFormModal(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.subject_id) errors.subject_id = t("common.ui.laMatiereEstRequise");
    if (!formData.title.trim()) errors.title = t("common.ui.leTitreEstRequis");
    if (!formData.stem.trim()) errors.stem = t("pages.clinicalCases.laMiseSituationVignetteEstRequise");
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        subject_id: formData.subject_id,
        title: formData.title.trim(),
        stem: formData.stem.trim(),
        is_active: formData.is_active,
      };

      let caseId = editingId;
      if (formMode === "add") {
        const created = await createClinicalCase(payload);
        caseId = created?.data?.data?.id;
        setFeedback({ type: "success", message: t("pages.clinicalCases.casCliniqueCreeAvecSucces") });
      } else {
        await updateClinicalCase(editingId, payload);
        setFeedback({ type: "success", message: t("pages.clinicalCases.casCliniqueMisJour") });
      }
      // L'image est envoyée une fois le cas enregistré (multipart séparé), comme pour les questions.
      if (caseId && (imageFile || removeImage)) {
        try {
          if (imageFile) await uploadClinicalCaseImage(caseId, imageFile);
          else await deleteClinicalCaseImage(caseId);
        } catch (imgErr) {
          setFeedback({
            type: "warning",
            message: getApiErrorMessage(imgErr, t(imageFile ? "pages.questions.imageUploadError" : "pages.questions.imageRemoveError")),
          });
        }
      }
      setShowFormModal(false);
      fetchCases();
    } catch (err) {
      setSubmitError(getApiErrorMessage(err, t("common.crud.saveError")));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteClinicalCase(deleteTarget.id);
      setFeedback({ type: "success", message: t("pages.clinicalCases.casCliniqueSupprime") });
      setDeleteTarget(null);
      if (cases.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchCases();
    } catch (err) {
      setFeedback({ type: "danger", message: getApiErrorMessage(err, t("pages.clinicalCases.impossibleSupprimerCasClinique")) });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  // --- Affectation des questions au cas clinique ---
  const openAssignModal = async (clinicalCase) => {
    setAssignCase(clinicalCase);
    setAssigned([]);
    setBank([]);
    setBankSearch("");
    setAssignError("");
    setAssignLoading(true);
    try {
      const res = await getClinicalCaseById(clinicalCase.id);
      setAssigned((res.data?.data?.questions || []).map((q) => ({ id: q.id, content: q.content, points: q.points })));
    } catch (err) {
      setAssignError(getApiErrorMessage(err, t("pages.clinicalCases.impossibleChargerQuestionsCasClinique")));
    } finally {
      setAssignLoading(false);
    }
  };

  // Banque de questions de la même matière (recherche différée)
  useEffect(() => {
    if (!assignCase) return undefined;
    const timer = setTimeout(async () => {
      try {
        const params = { page: 1, limit: 100, subject_id: assignCase.subject_id };
        if (bankSearch.trim()) params.search = bankSearch.trim();
        const res = await getQuestions(params);
        setBank(res.data?.data || []);
      } catch (err) {
        setBank([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [assignCase, bankSearch]);

  // Questions proposées : non déjà dans le cas, et libres (ou déjà rattachées à ce cas).
  const availableQuestions = bank.filter(
    (q) => !assigned.some((a) => a.id === q.id) && (!q.clinical_case_id || q.clinical_case_id === assignCase?.id)
  );
  const lockedCount = bank.filter((q) => q.clinical_case_id && q.clinical_case_id !== assignCase?.id).length;

  const addToCase = (q) => setAssigned((prev) => [...prev, { id: q.id, content: q.content, points: q.points }]);
  const removeFromCase = (id) => setAssigned((prev) => prev.filter((a) => a.id !== id));
  const moveInCase = (index, delta) =>
    setAssigned((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const saveAssignment = async () => {
    if (!assignCase) return;
    setAssignSaving(true);
    setAssignError("");
    try {
      await setClinicalCaseQuestions(assignCase.id, assigned.map((a) => a.id));
      setFeedback({ type: "success", message: t("pages.clinicalCases.assignSuccess") });
      setAssignCase(null);
      fetchCases();
    } catch (err) {
      setAssignError(getApiErrorMessage(err, t("pages.clinicalCases.assignError")));
    } finally {
      setAssignSaving(false);
    }
  };

  const openViewModal = async (clinicalCase) => {
    setViewCase({ ...clinicalCase, questions: [] });
    setViewLoading(true);
    try {
      const res = await getClinicalCaseById(clinicalCase.id);
      setViewCase(res.data?.data || { ...clinicalCase, questions: [] });
    } catch (err) {
      setFeedback({ type: "danger", message: t("pages.clinicalCases.impossibleChargerQuestionsCasClinique") });
    } finally {
      setViewLoading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("pages.clinicalCases.casCliniques")}</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="pe-1 mb-2">
              <button type="button" className="btn btn-outline-light bg-white btn-icon me-1" title={t("common.crud.refresh")} onClick={resetFilters}>
                <i className="ti ti-refresh"></i>
              </button>
            </div>
            {canCreate && (
            <div className="mb-2">
              <button type="button" className="btn btn-primary d-flex align-items-center" onClick={openAddModal}>
                <i className="ti ti-square-rounded-plus me-2"></i>{t("pages.clinicalCases.ajouterCasClinique")}
              </button>
            </div>
            )}
          </div>
        </div>

        <div className="alert alert-info">
          {t("pages.clinicalCases.lesCasCliniquesRegroupentPlusieursQuestions")}
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{t("pages.clinicalCases.listeCasCliniques")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="input-icon-start mb-3 me-2 position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("common.ui.rechercherTitre")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ minWidth: "220px" }}
                />
              </div>
              <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={subjectFilter}
                  onChange={(e) => {
                    setSubjectFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t("common.fields.allSubjects")}</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="card-body p-0 py-3">
            <div className="table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    <th>{t("common.ui.titre")}</th>
                    <th>{t("common.fields.subject")}</th>
                    <th>{t("pages.clinicalCases.vignette")}</th>
                    <th>{t("pages.clinicalCases.questionsColumn")}</th>
                    <th>{t("common.crud.statusColumn")}</th>
                    <th>{t("common.crud.actionColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("common.crud.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4 text-danger">
                        {loadError}
                      </td>
                    </tr>
                  ) : cases.length > 0 ? (
                    cases.map((clinicalCase) => (
                      <tr key={clinicalCase.id}>
                        <td className="fw-medium">{clinicalCase.title}</td>
                        <td>{label(subjects, clinicalCase.subject_id)}</td>
                        <td>
                          <span className="text-truncate d-inline-block" style={{ maxWidth: "320px" }} title={clinicalCase.stem}>
                            {clinicalCase.stem}
                          </span>
                        </td>
                        <td>
                          <span className="badge badge-soft-info">{t("pages.clinicalCases.questionsCount", { count: clinicalCase.questions_count ?? 0 })}</span>
                        </td>
                        <td>
                          <span className={`badge badge-soft-${clinicalCase.is_active ? "success" : "danger"} d-inline-flex align-items-center`}>
                            <i className="ti ti-circle-filled fs-5 me-1"></i>
                            {clinicalCase.is_active ? t("common.crud.statusActive") : t("common.crud.statusInactive")}
                          </span>
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              as="button"
                              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false"
                            >
                              <i className="ti ti-dots-vertical fs-14"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="p-3">
                              <Dropdown.Item className="rounded-1" onClick={() => openViewModal(clinicalCase)}>
                                <i className="ti ti-eye me-2"></i>{t("pages.clinicalCases.voirQuestionsLiees")}
                              </Dropdown.Item>
                              {canUpdate && (
                              <Dropdown.Item className="rounded-1" onClick={() => openAssignModal(clinicalCase)}>
                                <i className="ti ti-list-check me-2"></i>{t("pages.clinicalCases.assignQuestions")}
                              </Dropdown.Item>
                              )}
                              {canUpdate && (
                              <Dropdown.Item className="rounded-1" onClick={() => openEditModal(clinicalCase)}>
                                <i className="ti ti-edit-circle me-2"></i>{t("common.crud.edit")}
                              </Dropdown.Item>
                              )}
                              {canDelete && (
                              <Dropdown.Item className="rounded-1" onClick={() => setDeleteTarget(clinicalCase)}>
                                <i className="ti ti-trash-x me-2"></i>{t("common.crud.delete")}
                              </Dropdown.Item>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <i className="ti ti-search-off fs-1 text-muted"></i>
                        <h5 className="mt-2">{t("pages.clinicalCases.aucunCasCliniqueTrouve")}</h5>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center px-3 pt-3 border-top">
                <span className="text-sm text-gray-600">{t("pages.clinicalCases.casClinique", { total })}</span>
                <div className="d-flex gap-1">
                  <button className="btn btn-sm btn-outline-secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                    {t("common.ui.precedent")}
                  </button>
                  <button className="btn btn-sm btn-outline-secondary" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                    {t("common.ui.suivant")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered size="lg">
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{formMode === "add" ? t("pages.clinicalCases.ajouterCasClinique") : t("pages.clinicalCases.modifierCasClinique")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && <div className="alert alert-danger" style={{ whiteSpace: "pre-line" }}>{submitError}</div>}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  {t("common.fields.subject")} <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${formErrors.subject_id ? "is-invalid" : ""}`}
                  value={formData.subject_id}
                  onChange={(e) => handleFormChange("subject_id", e.target.value)}
                >
                  <option value="">{t("common.crud.selectPlaceholder")}</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {formErrors.subject_id && <div className="invalid-feedback d-block">{formErrors.subject_id}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  {t("common.ui.titre")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${formErrors.title ? "is-invalid" : ""}`}
                  value={formData.title}
                  onChange={(e) => handleFormChange("title", e.target.value)}
                />
                {formErrors.title && <div className="invalid-feedback d-block">{formErrors.title}</div>}
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">
                {t("pages.clinicalCases.vignetteMiseSituation")} <span className="text-danger">*</span>
              </label>
              <textarea
                className={`form-control ${formErrors.stem ? "is-invalid" : ""}`}
                rows={4}
                placeholder={t("pages.clinicalCases.exPatientFictif62AnsSe")}
                value={formData.stem}
                onChange={(e) => handleFormChange("stem", e.target.value)}
              ></textarea>
              {formErrors.stem && <div className="invalid-feedback d-block">{formErrors.stem}</div>}
              <div className="form-text">{t("pages.clinicalCases.toujoursFictifPedagogiqueJamaisDonneesReelles")}</div>
            </div>
            <div className="mb-3">
              <label className="form-label">{t("pages.clinicalCases.imageLabel")}</label>
              {(imagePreview || (existingImage && !removeImage)) && (
                <div className="mb-2 d-flex align-items-start gap-3">
                  <img
                    src={imagePreview || resolveUploadUrl(existingImage)}
                    alt={t("pages.questions.imageAlt")}
                    style={{ maxWidth: "260px", maxHeight: "180px", objectFit: "contain" }}
                    className="border rounded p-1 bg-white"
                  />
                  <button type="button" className="btn btn-outline-danger btn-sm" onClick={handleImageRemove} disabled={submitting}>
                    {t("pages.questions.imageRemove")}
                  </button>
                </div>
              )}
              <input
                type="file"
                className={`form-control ${imageError ? "is-invalid" : ""}`}
                accept={IMAGE_ACCEPT}
                onChange={handleImageSelect}
                disabled={submitting}
              />
              {imageError ? (
                <div className="invalid-feedback d-block">{imageError}</div>
              ) : (
                <small className="text-muted">{t("pages.questions.imageHint")}</small>
              )}
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="clinical-case-active"
                checked={formData.is_active}
                onChange={(e) => handleFormChange("is_active", e.target.checked)}
              />
              <label className="form-check-label" htmlFor="clinical-case-active">
                {t("pages.clinicalCases.casCliniqueActif")}
              </label>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={closeFormModal} disabled={submitting}>
              {t("common.crud.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t("common.crud.saving") : formMode === "add" ? t("common.crud.add") : t("common.crud.save")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={viewCase !== null} onHide={() => setViewCase(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t("pages.clinicalCases.questionsLiees", { title: viewCase?.title })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">{viewCase?.stem}</p>
          {viewCase?.image_url && (
            <img src={resolveUploadUrl(viewCase.image_url)} alt={viewCase.title} style={{ maxWidth: "100%", maxHeight: "220px", objectFit: "contain" }} className="border rounded p-1 bg-white mb-3" />
          )}
          {viewLoading ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t("common.crud.loading")}</span>
              </div>
            </div>
          ) : viewCase?.questions?.length ? (
            <ol className="list-group list-group-numbered">
              {viewCase.questions.map((q) => (
                <li key={q.id} className="list-group-item d-flex justify-content-between align-items-start">
                  <div className="ms-2 me-auto">
                    <div>{q.content}</div>
                    <small className="text-muted">
                      {t("pages.clinicalCases.ordre")} {q.case_order_index ?? "-"} · {t("pages.clinicalCases.pt", { points: q.points })}
                    </small>
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-muted mb-0">{t("pages.clinicalCases.aucuneQuestionNEstEncoreRattachee")}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-light" onClick={() => setViewCase(null)}>
            {t("common.crud.close")}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={assignCase !== null} onHide={() => (assignSaving ? null : setAssignCase(null))} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{t("pages.clinicalCases.assignTitle", { title: assignCase?.title })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {assignError && <div className="alert alert-danger" style={{ whiteSpace: "pre-line" }}>{assignError}</div>}
          <p className="text-muted small">{t("pages.clinicalCases.assignHint")}</p>
          <div className="row">
            <div className="col-lg-6 mb-3">
              <h6 className="mb-2">{t("pages.clinicalCases.assignedQuestions", { count: assigned.length })}</h6>
              {assignLoading ? (
                <div className="text-center py-3"><div className="spinner-border text-primary" role="status"></div></div>
              ) : assigned.length === 0 ? (
                <p className="text-muted border rounded p-3 mb-0">{t("pages.clinicalCases.noAssigned")}</p>
              ) : (
                <ol className="list-group list-group-numbered" style={{ maxHeight: "380px", overflowY: "auto" }}>
                  {assigned.map((q, i) => (
                    <li key={q.id} className="list-group-item d-flex align-items-start gap-2">
                      <div className="ms-1 me-auto">
                        <div>{q.content}</div>
                        <small className="text-muted">{t("pages.clinicalCases.pt", { points: q.points })}</small>
                      </div>
                      <div className="btn-group btn-group-sm flex-shrink-0">
                        <button type="button" className="btn btn-outline-secondary" title={t("pages.clinicalCases.moveUp")} disabled={i === 0 || assignSaving} onClick={() => moveInCase(i, -1)}>
                          <i className="ti ti-arrow-up"></i>
                        </button>
                        <button type="button" className="btn btn-outline-secondary" title={t("pages.clinicalCases.moveDown")} disabled={i === assigned.length - 1 || assignSaving} onClick={() => moveInCase(i, 1)}>
                          <i className="ti ti-arrow-down"></i>
                        </button>
                        <button type="button" className="btn btn-outline-danger" title={t("pages.clinicalCases.removeFromCase")} disabled={assignSaving} onClick={() => removeFromCase(q.id)}>
                          <i className="ti ti-x"></i>
                        </button>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
            <div className="col-lg-6 mb-3">
              <h6 className="mb-2">{t("pages.clinicalCases.availableQuestions")}</h6>
              <input
                type="text"
                className="form-control mb-2"
                placeholder={t("pages.clinicalCases.searchQuestion")}
                value={bankSearch}
                onChange={(e) => setBankSearch(e.target.value)}
              />
              {availableQuestions.length === 0 ? (
                <p className="text-muted border rounded p-3 mb-0">{t("pages.clinicalCases.noAvailable")}</p>
              ) : (
                <ul className="list-group" style={{ maxHeight: "340px", overflowY: "auto" }}>
                  {availableQuestions.map((q) => (
                    <li key={q.id} className="list-group-item d-flex align-items-start gap-2">
                      <div className="me-auto">{q.content}</div>
                      <button type="button" className="btn btn-sm btn-outline-primary flex-shrink-0" disabled={assignSaving} onClick={() => addToCase(q)}>
                        <i className="ti ti-plus me-1"></i>{t("pages.clinicalCases.addToCase")}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {lockedCount > 0 && <small className="text-muted d-block mt-2">{t("pages.clinicalCases.lockedHint", { count: lockedCount })}</small>}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-light" onClick={() => setAssignCase(null)} disabled={assignSaving}>
            {t("common.crud.cancel")}
          </button>
          <button type="button" className="btn btn-primary" onClick={saveAssignment} disabled={assignSaving || assignLoading}>
            {assignSaving ? t("common.crud.saving") : t("pages.clinicalCases.assignSave")}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={deleteTarget !== null} onHide={() => (deleting ? null : setDeleteTarget(null))} centered>
        <div className="modal-body text-center">
          <span className="delete-icon">
            <i className="ti ti-trash-x"></i>
          </span>
          <h4>{t("common.crud.confirmDeleteTitle")}</h4>
          <p>
            <Trans i18nKey="pages.clinicalCases.voulezVousVraimentSupprimerCasClinique" values={{ title: deleteTarget?.title }} components={[<strong />]} />
          </p>
          <div className="d-flex justify-content-center">
            <button type="button" className="btn btn-light me-3" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("common.crud.cancel")}
            </button>
            <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? t("common.crud.deleting") : t("common.crud.confirmDelete")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ClinicalCases;